const HERO_TEXT_FONT_SCALE_MIN = 0.7;
const HERO_TEXT_FONT_SCALE_MAX = 1.4;
const HERO_TEXT_FONT_SCALE_STEP = 0.05;

const HERO_TEXT_SCALE_PATH = /^(?:hero\.(?:eyebrow|title|subtitle)|hero\.slides\.\d+\.(?:title|subtitle))$/;

function normalizedText(value) {
  return String(value ?? "").trim();
}

export function longestTitleWordLength(value) {
  const words = normalizedText(value).split(/\s+/).filter(Boolean);
  return words.reduce((longest, word) => Math.max(longest, Array.from(word).length), 0);
}

export function titleWordFitScale(value) {
  const longest = longestTitleWordLength(value);
  if (longest >= 30) return 0.62;
  if (longest >= 24) return 0.7;
  if (longest >= 20) return 0.78;
  if (longest >= 16) return 0.87;
  if (longest >= 13) return 0.94;
  return 1;
}

export function heroTextLength(value) {
  return normalizedText(value).length;
}

export function heroTitleFontSize(value) {
  const length = heroTextLength(value);
  let size;

  if (length > 150) {
    size = {
      xs: "clamp(1.75rem, 8.5vw, 2.75rem)",
      sm: "clamp(2.3rem, 5.5vw, 3.7rem)",
      md: "clamp(2.8rem, 4.5vw, 4.8rem)",
    };
  } else if (length > 95) {
    size = {
      xs: "clamp(2rem, 9.5vw, 3.15rem)",
      sm: "clamp(2.8rem, 6vw, 4.4rem)",
      md: "clamp(3.4rem, 5vw, 5.6rem)",
    };
  } else if (length > 55) {
    size = {
      xs: "clamp(2.3rem, 10.5vw, 3.7rem)",
      sm: "clamp(3.2rem, 6.8vw, 5.25rem)",
      md: "clamp(4rem, 5.5vw, 6.4rem)",
    };
  } else {
    size = {
      xs: "clamp(2.8rem, 13vw, 4.6rem)",
      sm: "clamp(4rem, 8vw, 6.5rem)",
      md: "clamp(4.8rem, 6.5vw, 7.4rem)",
    };
  }
  return scaleResponsiveCss(size, titleWordFitScale(value));
}

export function heroEyebrowFontSize(baseFontSize = 16) {
  const numeric = Number(baseFontSize);
  const safeBase = Number.isFinite(numeric) ? numeric : 16;
  const size = `${Math.round((safeBase * 0.75 / 16) * 1000) / 1000}rem`;
  return { xs: size, sm: size, md: size };
}

export function heroSubtitleFontSize(value) {
  return heroTextLength(value) > 420
    ? { xs: ".94rem", sm: "1.04rem", md: "1.12rem" }
    : { xs: "1rem", sm: "1.15rem", md: "1.25rem" };
}

function scaleCssToken(token, factor) {
  if (typeof token !== "string") return token;
  return token.replace(/(-?\d*\.?\d+)(rem|px|vw|vh|svh)/g, (_, number, unit) => {
    const scaled = Math.round(Number(number) * factor * 1000) / 1000;
    return `${scaled}${unit}`;
  });
}

export function scaleResponsiveCss(value, factor = 1) {
  if (value == null) return value;
  if (typeof value === "string") return scaleCssToken(value, factor);
  if (typeof value === "number") return value * factor;
  if (Array.isArray(value)) return value.map((item) => scaleResponsiveCss(item, factor));
  if (typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, scaleResponsiveCss(item, factor)])
    );
  }
  return value;
}

export function isHeroTextFontScalePath(path) {
  return HERO_TEXT_SCALE_PATH.test(String(path || "").trim());
}

export function clampHeroTextFontScale(value, fallback = 1) {
  if (value === "" || value === null || value === undefined) return fallback;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  const clamped = Math.min(HERO_TEXT_FONT_SCALE_MAX, Math.max(HERO_TEXT_FONT_SCALE_MIN, numeric));
  return Math.round(clamped * 100) / 100;
}

export {
  HERO_TEXT_FONT_SCALE_MIN,
  HERO_TEXT_FONT_SCALE_MAX,
  HERO_TEXT_FONT_SCALE_STEP,
};
